import { ChangeEvent } from 'react';
import Layout from 'components/Layout';
import PremiumIcon from 'components/PremiumIcon';
import {
    ApplicationResult,
    ContactResult,
    EventResult,
    TaskResult,
    useGlobalSearchQuery,
} from 'core/api/globalSearch';
import useDebounce from 'hooks/debounce';
import { useIsPremium } from 'hooks/userIsPremium';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getNumericDate } from 'utils/dateTimeFormat';
import {
    SEmptyState,
    SHeader,
    SPremiumBadge,
    SPremiumNotice,
    SResultCard,
    SResultCardContent,
    SResultDate,
    SResultSnippet,
    SResultSubtitle,
    SResultTitle,
    SSearchBar,
    SSection,
    SSectionTitle,
    SSubtitle,
    STag,
    STagList,
    STitle,
    SWrapper,
} from './styles';

const ContactCard: FC<{ result: ContactResult }> = ({ result }) => {
    const { t } = useTranslation();
    const name = `${t(`common.civility.enum.${result.civility}`)} ${result.firstName ?? ''} ${result.lastName}`.trim();
    const subtitle = [result.job, result.company].filter(Boolean).join(' · ');
    const tags = result.tags ?? [];

    return (
        <SResultCard to={`/contact/${result.id}`}>
            <SResultCardContent>
                <SResultTitle>{name}</SResultTitle>
                {subtitle && <SResultSubtitle>{subtitle}</SResultSubtitle>}
                {result.comments && <SResultSnippet>{result.comments}</SResultSnippet>}
                {tags.length > 0 && (
                    <STagList>
                        {tags.map((tag) => (
                            <STag key={tag.id}>{tag.name}</STag>
                        ))}
                    </STagList>
                )}
            </SResultCardContent>
            <SResultDate>{`${t('common.added-at')} ${getNumericDate(result.createdDate)}`}</SResultDate>
        </SResultCard>
    );
};

const TaskCard: FC<{ result: TaskResult }> = ({ result }) => {
    const { t } = useTranslation();
    const title = result.customSubject || t(`common.task-subject.enum.${result.subject}`);
    const contactName = result.contact
        ? `${result.contact.firstName ?? ''} ${result.contact.lastName}`.trim()
        : '';

    return (
        <SResultCard to={`/task/${result.id}`}>
            <SResultCardContent>
                <SResultTitle>{title}</SResultTitle>
                {contactName && <SResultSubtitle>{contactName}</SResultSubtitle>}
                {result.comments && <SResultSnippet>{result.comments}</SResultSnippet>}
                {!result.comments && result.markAsCompleteComment && (
                    <SResultSnippet>{result.markAsCompleteComment}</SResultSnippet>
                )}
            </SResultCardContent>
            <SResultDate>{`${t('common.added-at')} ${getNumericDate(result.createdDate)}`}</SResultDate>
        </SResultCard>
    );
};

const EventCard: FC<{ result: EventResult }> = ({ result }) => {
    const { t } = useTranslation();
    const contactName = result.contact
        ? `${result.contact.firstName ?? ''} ${result.contact.lastName}`.trim()
        : '';
    const title = t(`common.${result.type}-subject.enum.${result.subject}`);
    const subtitle = [contactName, result.mediaDetails].filter(Boolean).join(' · ');

    return (
        <SResultCard to={`/event/${result.id}`}>
            <SResultCardContent>
                <SResultTitle>{title}</SResultTitle>
                {subtitle && <SResultSubtitle>{subtitle}</SResultSubtitle>}
                {result.comments && <SResultSnippet>{result.comments}</SResultSnippet>}
                {!result.comments && result.markAsCompleteComment && (
                    <SResultSnippet>{result.markAsCompleteComment}</SResultSnippet>
                )}
            </SResultCardContent>
            <SResultDate>{`${t('common.added-at')} ${getNumericDate(result.createdDate)}`}</SResultDate>
        </SResultCard>
    );
};

const ApplicationCard: FC<{ result: ApplicationResult }> = ({ result }) => {
    const { t } = useTranslation();
    const tags = result.tags ?? [];
    const subtitle = [
        result.job,
        result.place,
        result.reference && `${t('application.reference.label')} : ${result.reference}`,
        result.sourceDetails && `${t('application.source-details.label')} : ${result.sourceDetails}`,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <SResultCard to={`/application/${result.id}`}>
            <SResultCardContent>
                <SResultTitle>{result.company}</SResultTitle>
                {subtitle && <SResultSubtitle>{subtitle}</SResultSubtitle>}
                {result.comment && <SResultSnippet>{result.comment}</SResultSnippet>}
                {!result.comment && result.description && <SResultSnippet>{result.description}</SResultSnippet>}
                {tags.length > 0 && (
                    <STagList>
                        {tags.map((tag) => (
                            <STag key={tag.id}>{tag.name}</STag>
                        ))}
                    </STagList>
                )}
            </SResultCardContent>
            <SResultDate>{`${t('common.added-at')} ${getNumericDate(result.createdDate)}`}</SResultDate>
        </SResultCard>
    );
};

const GlobalSearch: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const isPremium = useIsPremium();

    const initialQuery = new URLSearchParams(location.search).get('q') ?? '';

    const [inputValue, setInputValue] = useState(initialQuery);
    const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);

    const updateQuery = useDebounce((val: string) => {
        setDebouncedQuery(val);
        const params = new URLSearchParams();
        if (val) {
            params.set('q', val);
        }
        history.replace({ pathname: '/search', search: params.toString() });
    }, 400);

    const { data } = useGlobalSearchQuery(debouncedQuery, isPremium);

    useEffect(() => {
        if (!isPremium && initialQuery.trim().length > 0) {
            history.replace('/ask-subscription');
        }
        // Only check once on mount, against the query the page was loaded with.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFocus = () => {
        if (!isPremium) {
            history.push('/ask-subscription');
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        updateQuery(val);
    };

    const contacts = data?.contacts ?? [];
    const tasks = data?.tasks ?? [];
    const events = data?.events ?? [];
    const applications = data?.applications ?? [];

    const hasQuery = debouncedQuery.trim().length >= 3;
    const hasResults = contacts.length + tasks.length + events.length + applications.length > 0;

    return (
        <Layout pageTitle={t('page.global-search.meta-title')}>
            <SWrapper>
                <SHeader>
                    <STitle>{t('page.global-search.title')}</STitle>
                    <SSubtitle>{t('page.global-search.subtitle')}</SSubtitle>
                </SHeader>

                <SSearchBar>
                    <SPremiumBadge>
                        <PremiumIcon />
                    </SPremiumBadge>
                    <input
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder={t('page.global-search.placeholder')}
                        type="search"
                        value={inputValue}
                    />
                </SSearchBar>

                {!isPremium && (
                    <SPremiumNotice to="/ask-subscription">
                        {t('page.global-search.premium-required')}
                    </SPremiumNotice>
                )}

                {hasQuery && !hasResults && (
                    <SEmptyState>{t('page.global-search.no-results')}</SEmptyState>
                )}

                {contacts.length > 0 && (
                    <SSection>
                        <SSectionTitle>{t('common.contacts')}</SSectionTitle>
                        {contacts.map((c) => (
                            <ContactCard key={c.id} result={c} />
                        ))}
                    </SSection>
                )}

                {(tasks.length > 0 || events.length > 0) && (
                    <SSection>
                        <SSectionTitle>{t('common.activities')}</SSectionTitle>
                        {tasks.map((t) => (
                            <TaskCard key={t.id} result={t} />
                        ))}
                        {events.map((e) => (
                            <EventCard key={e.id} result={e} />
                        ))}
                    </SSection>
                )}

                {applications.length > 0 && (
                    <SSection>
                        <SSectionTitle>{t('common.applications')}</SSectionTitle>
                        {applications.map((a) => (
                            <ApplicationCard key={a.id} result={a} />
                        ))}
                    </SSection>
                )}
            </SWrapper>
        </Layout>
    );
};

export default GlobalSearch;
